import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  TextField,
  CircularProgress,
} from "@mui/material";
import axios from "axios";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import type { CheckAccessResponse } from "../../../api/signer";
import { signatureCheckAccess } from "../../../api/signer";

interface CheckAccessModalProps {
  open: boolean;
  linkCode: string;
  onSuccess: (data: CheckAccessResponse) => void;
}

export default function CheckAccessModal({
  open,
  linkCode,
  onSuccess,
}: CheckAccessModalProps): React.JSX.Element {
  const { t } = useTranslation();
  const [digits, setDigits] = useState("");
  const [errorField, setErrorField] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (): Promise<void> => {
    const cleanDigits = digits.trim().replace(/[^a-zA-Z0-9]/g, "").toUpperCase();
    if (!cleanDigits) {
      setErrorField(t("checkAccess.invalidLength", "Informe o CPF/CNPJ do assinante."));
      return;
    }

    setIsSubmitting(true);
    setErrorField("");
    try {
      const { data } = await signatureCheckAccess(linkCode, cleanDigits);
      onSuccess(data);
    } catch (e) {
      if (axios.isAxiosError(e)) {
        const responseData = e.response?.data as {
          code?: string;
          userMessage?: string;
          message?: string;
        } | undefined;

        if (
          responseData?.code === "S1225" ||
          responseData?.code === "S1202" ||
          responseData?.message?.includes("S1225")
        ) {
          setErrorField(t("checkAccess.incorrectDigits", "O CPF/CNPJ do assinante está incorreto."));
        } else {
          setErrorField(
            responseData?.userMessage ||
              responseData?.message ||
              t("checkAccess.incorrectDigits", "O CPF/CNPJ do assinante está incorreto."),
          );
        }
      } else {
        setErrorField(t("checkAccess.incorrectDigits", "O CPF/CNPJ do assinante está incorreto."));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const val = event.target.value;
    setDigits(val);
    if (errorField) {
      setErrorField("");
    }
  };

  return (
    <Dialog fullWidth maxWidth="sm" open={open}>
      <DialogTitle sx={{ fontWeight: "600", fontSize: 24, pb: 1.5, color: "primary.main" }}>
        {t("checkAccess.title", "Validação de Acesso")}
      </DialogTitle>
      <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 3, pt: 2 }}>
        <Typography
          sx={{ fontWeight: "500", fontSize: 18, color: "text.primary" }}
        >
          {t(
            "checkAccess.description",
            "Para visualizar e assinar o documento, informe o CPF/CNPJ do assinante.",
          )}
        </Typography>
        <TextField
          autoFocus
          error={Boolean(errorField)}
          fullWidth
          helperText={errorField}
          InputLabelProps={{
            sx: {
              color: "text.secondary",
              fontWeight: 600,
              "&.Mui-focused": {
                color: "primary.main",
              },
            },
          }}
          label={t("checkAccess.digitsLabel", "CPF/CNPJ do assinante")}
          onChange={handleChange}
          onKeyDown={(e) => {
            if (e.key === "Enter" && digits.trim().length > 0 && !isSubmitting) {
              void handleSubmit();
            }
          }}
          placeholder="Informe o CPF/CNPJ do assinante"
          value={digits}
          variant="outlined"
        />
      </DialogContent>
      <DialogActions sx={{ padding: "16px 24px" }}>
        <Button
          color="primary"
          disabled={!digits.trim() || isSubmitting}
          onClick={() => void handleSubmit()}
          sx={{ borderRadius: "12px", width: "100%" }}
          variant="contained"
        >
          {isSubmitting ? <CircularProgress size={20} /> : t("checkAccess.submit", "Acessar Documento")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
